import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import parse from 'html-react-parser';
import {colors, gradients, borderRadius, spacing, fontSize, fontWeight, lineHeight, overlay, transition} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

interface Web3AlsoAvailableProps {
    heading: string;
    services: string[];
    buttonText?: string;
    openForm: () => void;
}

const cardSx = {
    background: gradients.navyCard,
    borderRadius: borderRadius['4xl'],
    boxShadow:
        '0px 10px 26px -2.5px rgba(35,46,79,0.05), 0px 2.289px 5.95px -0.67px rgba(35,46,79,0.05), 0px 3.6px 7.57px 3.17px rgba(35,46,79,0.05)',
    position: 'relative',
    overflow: 'hidden',
    px: {xs: spacing[24], md: spacing[60]},
    py: {xs: spacing[40], md: spacing[60]},
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing[32]
} as const;

const borderOverlaySx = {
    position: 'absolute',
    inset: 0,
    border: `4px solid ${overlay.whiteFaint}`,
    borderRadius: borderRadius['4xl'],
    pointerEvents: 'none'
} as const;

const headingSx = {
    fontSize: {xs: fontSize[28], md: '30px'},
    fontWeight: fontWeight.bold,
    letterSpacing: '-2.24px',
    lineHeight: '59px',
    textAlign: 'center',
    color: colors.text.inverse
} as const;

const pillsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[12],
    justifyContent: 'center'
} as const;

const pillSx = {
    bgcolor: colors.background.default,
    border: '1px solid transparent',
    borderRadius: borderRadius.full,
    px: '21px',
    py: '9px',
    cursor: 'pointer',
    userSelect: 'none',
    transition: `background-color ${transition.base}, border-color ${transition.base}, transform ${transition.fast}`,
    '&:hover': {
        bgcolor: 'transparent',
        borderColor: colors.text.inverse,
        transform: 'translateY(-1px)',
        '& .MuiTypography-root, & a': {
            color: colors.text.inverse
        }
    },
    '&:active': {
        transform: 'translateY(0)'
    }
};

const pillTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.primary.royalNavy,
    letterSpacing: '0.35px',
    lineHeight: lineHeight[20],
    whiteSpace: 'nowrap',
    transition: `color ${transition.base}`,
    '& a': {
        color: 'inherit',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'none'
        }
    }
};

const Web3AlsoAvailable = ({heading, services, buttonText, openForm}: Web3AlsoAvailableProps) => {
    return (
        <MUIContainer>
            <Box sx={cardSx}>
                <Box sx={borderOverlaySx} />
                <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                    {heading}
                </Typography>
                <Box sx={pillsWrapSx}>
                    {services.map((service) => (
                        <Box key={service} sx={pillSx}>
                            <Typography sx={pillTextSx}>{parse(service)}</Typography>
                        </Box>
                    ))}
                </Box>
                {buttonText && (
                    <MUIButton
                        label={buttonText}
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowForwardIcon />}
                        disableRipple={true}
                        className="animated-button moving-icon-button"
                        sx={nftButtonSx}
                        onClick={openForm}
                    />
                )}
            </Box>
        </MUIContainer>
    );
};

export default memo(Web3AlsoAvailable);
